import type { MicrosoftLoginRequest } from '@psspl/types';

import {
  clearMicrosoftCache,
  isMicrosoftSignInError,
  prepareMicrosoftLogin,
  signInWithMicrosoftPopup,
} from '../auth/msal';
import { safeReturnPath } from '../lib/url';
import { isApiClientError } from './api-error';
import { parseAuthUser, parseSession } from './auth-contract';
import { httpClient, setAccessTokenReader, setSessionRefresher } from './http-client';
import type { SessionUser, SignInFailure } from '../types';

/**
 * Authentication, per `docs/api.md` §4 and ADR-29.
 *
 * Sign-in is Microsoft Entra ID only. Microsoft's hosted page opens in a popup
 * (`auth/msal.ts`) and yields an ID token, and this service posts that token
 * once to `POST /api/auth/microsoft`. The API verifies it — signature, audience,
 * issuer, tenant — and answers with a Prisco session. The portal never decodes
 * or trusts the Microsoft token itself; it is dropped after the request.
 *
 * Session model (§4.1), unchanged by SSO (ADR-24):
 *  - the **access token** comes back in the sign-in/refresh body and is held here, in
 *    module memory only — never `localStorage`, `sessionStorage` or a readable
 *    cookie, so an XSS bug cannot lift a stored session;
 *  - the **refresh token** is an `HttpOnly` `phub_refresh` cookie the browser sends
 *    to `/api/auth/*` by itself. The portal never sees it.
 *
 * Components and the auth provider call these functions; nothing else touches the
 * auth endpoints.
 */

let accessToken: string | null = null;

export const accessTokenStore = {
  get: (): string | null => accessToken,
  set: (token: string | null): void => {
    accessToken = token;
  },
  clear: (): void => {
    accessToken = null;
  },
} as const;

setAccessTokenReader(accessTokenStore.get);

/**
 * `POST /api/auth/refresh`, single-flight and never aborted.
 *
 * Refresh tokens rotate, and replaying an old one revokes the whole session (§4.1).
 * Two refreshes racing with the same cookie — React StrictMode mounting the
 * provider twice, or several requests expiring together — would look like a replay.
 * Aborting one mid-flight is no safer: the server may already have rotated the
 * cookie while the browser drops the response carrying the new one. So every caller
 * shares one in-flight request, and it runs to completion.
 *
 * Resolves the user on success and `null` when there is no valid session. Any other
 * failure (network, 5xx) rejects, because it does not tell us whether a session
 * exists.
 */
let refreshInFlight: Promise<SessionUser | null> | null = null;

function refreshSession(): Promise<SessionUser | null> {
  refreshInFlight ??= (async () => {
    try {
      const session = parseSession(await httpClient.post<unknown>('/auth/refresh'));
      accessTokenStore.set(session.accessToken);
      return session.user;
    } catch (error) {
      accessTokenStore.clear();

      if (isApiClientError(error) && error.isSessionFailure) {
        return null;
      }

      throw error;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

// Refresh-on-401 hook for the HTTP client: `true` means "retry the request".
setSessionRefresher(async () => (await refreshSession().catch(() => null)) !== null);

/** A sign-in attempt that ended without a session, and why. */
export class SignInError extends Error {
  public readonly failure: SignInFailure;

  constructor(failure: SignInFailure) {
    super(`Sign-in failed: ${failure}`);
    this.name = 'SignInError';
    this.failure = failure;
  }
}

export function isSignInError(value: unknown): value is SignInError {
  return value instanceof SignInError;
}

/**
 * Maps a failed sign-in to what the screen may say (ADR-29 steps 4–5). Branches on
 * the shared error code only. Everything not named here — a token that failed
 * verification, a foreign tenant, `SSO_IDENTITY_CONFLICT`, `SSO_EMAIL_MISMATCH`, a
 * 5xx — is one `rejected`, so the screen never reveals which check failed.
 */
function toSignInFailure(error: unknown): SignInFailure {
  if (isMicrosoftSignInError(error)) {
    switch (error.kind) {
      case 'not-configured':
        return 'not-configured';
      case 'cancelled':
        return 'cancelled';
      case 'popup-blocked':
        return 'popup-blocked';
      case 'in-progress':
      case 'failed':
        return 'microsoft-failed';
    }
  }

  if (!isApiClientError(error)) {
    return 'rejected';
  }

  switch (error.code) {
    case 'SSO_IDENTITY_NOT_PROVISIONED':
      return 'not-provisioned';
    case 'ACCOUNT_INACTIVE':
      return 'inactive';
    case 'RATE_LIMITED':
      return 'rate-limited';
    case 'SERVICE_UNAVAILABLE':
      return 'unavailable';
    case 'NOT_FOUND':
      return 'endpoint-missing';
    default:
      return 'rejected';
  }
}

/** A completed Microsoft sign-in. */
export interface MicrosoftSignIn {
  readonly user: SessionUser;
  /** Validated path requested before sign-in, or `null`. */
  readonly returnTo: string | null;
}

/**
 * Exchanges a Microsoft ID token for a Prisco session: `POST /api/auth/microsoft`
 * with `{ idToken }` (ADR-29). The server sets the refresh cookie and returns the
 * access token. The ID token is passed straight to the transport — never logged,
 * never stored.
 *
 * The signed-in user is then read with `GET /api/auth/me` (§4.6), so the portal
 * routes on the account as the database holds it, through the same call every
 * later screen uses.
 */
async function exchangeMicrosoftToken(idToken: string): Promise<SessionUser> {
  const body: MicrosoftLoginRequest = { idToken };
  const session = parseSession(await httpClient.post<unknown>('/auth/microsoft', body));

  accessTokenStore.set(session.accessToken);

  return parseAuthUser(await httpClient.get<unknown>('/auth/me'));
}

let sessionStartInFlight: Promise<SessionUser | null> | null = null;
let signInInFlight: Promise<MicrosoftSignIn> | null = null;

export const authService = {
  /**
   * Restores an existing session from the refresh cookie when the portal starts
   * (§4.1). Resolves `null` when not signed in; a network failure rejects with the
   * `ApiClientError`, because it does not say whether a session exists.
   */
  startSession(): Promise<SessionUser | null> {
    sessionStartInFlight ??= refreshSession().finally(() => {
      sessionStartInFlight = null;
    });

    return sessionStartInFlight;
  },

  /** Loads MSAL before "Continue with Microsoft" is pressed, so the popup is not blocked. */
  prepareMicrosoftSignIn(): void {
    prepareMicrosoftLogin();
  },

  /**
   * "Continue with Microsoft": signs in to Microsoft in a popup, then exchanges the
   * ID token once for a Prisco session. Single-flight, because the ID token can be
   * exchanged only once and MSAL allows one interaction at a time. A rejection is
   * a `SignInError`.
   */
  signInWithMicrosoft(returnTo: string | null): Promise<MicrosoftSignIn> {
    signInInFlight ??= (async (): Promise<MicrosoftSignIn> => {
      let idToken: string;

      try {
        idToken = await signInWithMicrosoftPopup();
      } catch (error) {
        throw new SignInError(toSignInFailure(error));
      }

      try {
        return { user: await exchangeMicrosoftToken(idToken), returnTo: safeReturnPath(returnTo) };
      } catch (error) {
        accessTokenStore.clear();
        throw new SignInError(toSignInFailure(error));
      }
    })().finally(() => {
      signInInFlight = null;
    });

    return signInInFlight;
  },

  /**
   * `GET /api/auth/me` (§4.6). Read fresh from the database, so a role or capability
   * change made by an administrator shows up without signing in again.
   */
  async getCurrentUser(signal?: AbortSignal): Promise<SessionUser> {
    return parseAuthUser(await httpClient.get<unknown>('/auth/me', { signal }));
  },

  /**
   * `POST /api/auth/logout` (§4.5): `204`, revokes this session and clears the
   * cookie.
   *
   * Local credentials are cleared whatever happens, and MSAL's memory cache with
   * them — but Microsoft's own session is left alone (ADR-33 rule 4). A `401` means
   * the server session is already unusable, which §4.5 says to treat as signed out,
   * so only a network or server failure reports `serverConfirmed: false`.
   */
  async logout(): Promise<{ readonly serverConfirmed: boolean }> {
    try {
      await httpClient.post<unknown>('/auth/logout');
      return { serverConfirmed: true };
    } catch (error) {
      return { serverConfirmed: isApiClientError(error) && error.isSessionFailure };
    } finally {
      accessTokenStore.clear();
      await clearMicrosoftCache();
    }
  },
} as const;
